import { useState, type JSX } from 'react';
import { Text, Box, theme } from '@primerai/thread';
import { Controller } from 'react-hook-form';
import {
  ViewablePasswordInputWrapper,
  StyledInputDecorator,
} from '../input.styles';
import { TEXT_SIZE } from '~/constants';

const ICON_SIZE = 17;

export const iconDecorator = (Component: React.FC) => (props: any) => {
  const { icon: Icon, ...rest } = props;

  const iconStyle = {
    position: 'absolute',
    top: '50%',
    left: `${ICON_SIZE / 2}px`,
    marginTop: `-${ICON_SIZE / 2}px`,
  };
  const boxStyle = {
    minHeight: `${ICON_SIZE}px`,
    paddingLeft: `${ICON_SIZE * 1.5}px`,
  };

  return (
    <Box position="relative" style={boxStyle}>
      <Icon color="$grayScale.g5" size={ICON_SIZE} style={iconStyle} />
      <Component {...rest} />
    </Box>
  );
};

export const togglePasswordDecorator =
  (Component: React.FC) => (props: any) => {
    const [showPassword, setShowPassword] = useState(true);
    return (
      <ViewablePasswordInputWrapper>
        <Component
          {...props}
          hasIcon={false}
          type={showPassword ? 'text' : 'password'}
        />
        <Text
          className="display-toggle"
          onClick={() => setShowPassword(!showPassword)}
          size={TEXT_SIZE.DEFAULT}
        >
          {showPassword ? 'Hide' : 'Show'}
        </Text>
      </ViewablePasswordInputWrapper>
    );
  };

export const passwordDecorator = (Component: React.FC) => (props: any) => (
  <Box>
    <Component {...props} hasIcon={false} type="password" />
  </Box>
);

export const inputDecorator = (Component: React.FC) => (props: any) => (
  <StyledInputDecorator error={props.error}>
    <Component {...props} />
  </StyledInputDecorator>
);

export const labelDecorator = (Component: React.FC) => (props: any) => {
  const { id, label, ...rest } = props;
  if (!label) {
    return <Component {...props} />;
  }
  return (
    <>
      <label htmlFor={id}>
        <Box pb="$2">
          <Text
            style={{
              color: theme.colors.$grayScale.g9,
              fontWeight: theme.fontWeights.$semibold,
            }}
          >
            {label}
          </Text>
        </Box>
      </label>
      <Component id={id} {...rest} />
    </>
  );
};

export const errorDecorator =
  (Component: React.FC) =>
  ({ showError = true, ...props }: any) => (
    <Box>
      <Component {...props} />
      {props.error && showError && (
        <Text color="$alerts.danger" size={TEXT_SIZE.DEFAULT}>
          {props.error}&nbsp;
        </Text>
      )}
    </Box>
  );

export const renderError = (message: string) => (
  <Box>
    <Text color="$alerts.danger" size={TEXT_SIZE.DEFAULT}>
      {message}
    </Text>
  </Box>
);

export const reactFormDecorator =
  (Component: React.FC) =>
  (props: any): JSX.Element => {
    const { control, name, rules, errors } = props;
    const { message } = errors[name] || { message: '' };
    const controllerProps = { control, name, rules };
    return (
      <Box width="100%">
        <Controller
          {...controllerProps}
          render={({ field }) => (
            <Component {...field} {...props} error={message} />
          )}
        />
      </Box>
    );
  };
